import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from "react";
import { useForm, UseFormValues } from "../../../@vendor/utils/hooks/use-form";

type BookingCounselingContextType = {
  currentStep: number;
  onPrevStep: () => void;
  onNextStep: () => void;
  resetStep: () => void;
  form: UseFormValues<any>;
  resetForm: () => void;
  toStep: (value: number) => void;
  isPatientEmailValid: boolean;
  isPatientPhoneValid: boolean;
  changePatientEmailValid: (value: boolean) => void;
  changePatientPhoneValid: (value: boolean) => void;
};

const BookingCounselingContext = createContext<
  BookingCounselingContextType | undefined
>(undefined);

export const BookingCounselingProvider = (props: PropsWithChildren) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const toStep = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);
  const resetStep = useCallback(() => {
    setCurrentStep(0);
  }, []);
  const onPrevStep = useCallback(() => {
    if (currentStep < 0) return;
    setCurrentStep(currentStep - 1);
  }, [currentStep]);
  const onNextStep = useCallback(() => {
    if (currentStep > 1) return;
    setCurrentStep(currentStep + 1);
  }, [currentStep]);

  const defaultForm = {
    psychologist_id: null,
    type: "Online",
    category_id: null,
    session: "single-session",
    category: null,
    discount: 0,
    customer: null,
    psychologist: null,
    schedule_id: null,
    schedule_dates: [],
    schedule_date: null,
    schedule_times: [],
    schedule_duration: null,
    schedule_ids: [],
    schedules: [],
    time_ids: [],
    time: [],
    patient: null,
  };

  const [isPatientEmailValid, setIsPatientEmailValid] = useState<boolean>(true);
  const [isPatientPhoneValid, setIsPatientPhoneValid] = useState<boolean>(true);
  const changePatientEmailValid = (value: boolean) =>
    setIsPatientEmailValid(value);
  const changePatientPhoneValid = (value: boolean) =>
    setIsPatientPhoneValid(value);

  const form = useForm(defaultForm);

  const resetForm = () => {
    form.setFormValues(defaultForm);
  };

  return (
    <BookingCounselingContext.Provider
      value={{
        currentStep,
        onPrevStep,
        onNextStep,
        form,
        resetForm,
        resetStep,
        toStep,
        isPatientEmailValid,
        isPatientPhoneValid,
        changePatientEmailValid,
        changePatientPhoneValid,
      }}
    >
      {props.children}
    </BookingCounselingContext.Provider>
  );
};

export const useBookingCounseling = () => {
  const ctx = useContext(BookingCounselingContext);
  if (!ctx)
    throw new Error(
      "useBookingCounseling must be used within a BookingCounselingProvider"
    );
  return ctx;
};
